import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const LocationContext = createContext(null);

export const useGeo = () => useContext(LocationContext);

async function reverseGeocode(lat, lng) {
  const r = await fetch(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
  );
  const d = await r.json();
  const city = d.city || d.locality || d.principalSubdivision || "";
  const state = d.principalSubdivisionCode?.split("-")[1] || d.principalSubdivision || "";
  return { city, state };
}

export function LocationProvider({ children }) {
  const [geo, setGeo] = useState({ city: "", state: "", lat: null, lng: null, detected: false });
  const [detecting, setDetecting] = useState(false);
  const asked = useRef(false);

  const requestLocation = useCallback((cb = {}) => {
    if (!navigator.geolocation) { cb.onUnsupported?.(); return; }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const { city, state } = await reverseGeocode(latitude, longitude);
          setGeo({ city, state, lat: latitude, lng: longitude, detected: true });
          cb.onDetected?.({ city, state, lat: latitude, lng: longitude });
        } catch {
          setGeo((g) => ({ ...g, lat: latitude, lng: longitude }));
          cb.onError?.();
        } finally { setDetecting(false); }
      },
      () => { setDetecting(false); cb.onDenied?.(); },
      { timeout: 8000 }
    );
  }, []);

  // Ask for location shortly after the site opens (once).
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    const id = setTimeout(() => requestLocation({ auto: true }), 2000);
    return () => clearTimeout(id);
  }, [requestLocation]);

  return (
    <LocationContext.Provider value={{ ...geo, detecting, requestLocation }}>
      {children}
    </LocationContext.Provider>
  );
}
